import { StationInLineInsertion } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';

import { assemblyGroupsSlice } from '../store/assemblyGroupsSlice';
import { linesSlice } from '../store/linesSlice';
import { stationsSlice } from '../store/stationsSlice';
import { trailersSlice } from '../store/trailersSlice';
import { truckDriversSlice } from '../store/truckDriversSlice';
import { showCreateAssemblyGroupDialog } from './dialogActions';
import { AsyncThunkActionVoid, ThunkActionVoid } from './thunkActions';

export const startApp = (): ThunkActionVoid => (dispatch) => void dispatch(loadDeferredData());

const LINES_LOAD_TIMEOUT = 5_000;

export function loadDeferredData(): AsyncThunkActionVoid {
  return async function loadDeferredDataThunkAction(dispatch) {
    function handleCatastrophicStationLineLoadingFailure() {
      dispatch(linesSlice.actions.loadingCatastrophicallyFailed());
      dispatch(stationsSlice.actions.loadingCatastrophicallyFailed());
    }

    const params = new URLSearchParams(window.location.search);
    const startCreateAssemblyGroupForLookupCode = params.get('startCreateAssemblyGroupForLookupCode');
    if (startCreateAssemblyGroupForLookupCode) {
      dispatch(showCreateAssemblyGroupDialog(startCreateAssemblyGroupForLookupCode));
    }

    const spinnerId = dispatch(addSpinnerMessage('Loading...'));

    const loadAssemblyGroupsPromise = designService.assemblyGroups
      .getAll()
      .then((assemblyGroups) => void dispatch(assemblyGroupsSlice.actions.loadedFromServer(assemblyGroups)))
      .catch(() => dispatch(assemblyGroupsSlice.actions.loadingCatastrophicallyFailed()));

    const timeoutHandle = setTimeout(handleCatastrophicStationLineLoadingFailure, LINES_LOAD_TIMEOUT);

    try {
      const linesTask = designService.lines.getEagerlyLoadedLinesAndDirectoriesForCurrentOrganization();
      const stationsTask = designService.stations.getStationsForOrganizationShallow();

      Promise.all([linesTask, stationsTask]).then(() => clearTimeout(timeoutHandle));

      const { lines, directories } = await linesTask;

      const stationRelationships: Record<string, readonly StationInLineInsertion[]> = {};

      for (const lineWithStation of lines) {
        stationRelationships[lineWithStation.line.guid] = lineWithStation.stations;
      }

      dispatch(
        linesSlice.actions.loadedFromServer({
          lines: lines.map((lineWithStations) => lineWithStations.line),
          stationRelationships,
          directories,
        }),
      );
      dispatch(stationsSlice.actions.loadedFromServer({ stations: await stationsTask, shallow: true }));
    } catch (error) {
      console.error(error);
      handleCatastrophicStationLineLoadingFailure();
    }

    const trailersTask = designService.trailers.getAll();
    const truckDriversTask = designService.truckDrivers.getAll();

    try {
      const trailers = await trailersTask;
      dispatch(trailersSlice.actions.loadedFromServer(trailers));
    } catch {
      dispatch(trailersSlice.actions.loadingCatastrophicallyFailed());
    }

    try {
      const truckDrivers = await truckDriversTask;
      dispatch(truckDriversSlice.actions.loadedFromServer(truckDrivers));
    } catch {
      dispatch(truckDriversSlice.actions.loadingCatastrophicallyFailed());
    }

    await loadAssemblyGroupsPromise;

    dispatch(removeSpinnerMessage(spinnerId));
  };
}
